import React, { useEffect } from "react";
import { useProject } from "@/context/ProjectContext";
import { uid } from "@/lib/uid";
import { SectionCard, inputCls } from "./ui";
import { Plus, Trash2, Stethoscope } from "lucide-react";

const BEM_DEFAULTS = [
  { category: "Information / Data / Feedback", diagnosis: "", tag: "Non-Instructional" },
  { category: "Resources / Tools / Processes", diagnosis: "", tag: "Non-Instructional" },
  { category: "Incentives / Consequences", diagnosis: "", tag: "Non-Instructional" },
];

const TAGS = ["Instructional", "Non-Instructional"];

// Intervention diagnosis prepopulated with Gilbert's BEM environmental causes.
export default function InterventionDiagnosis() {
  const { phaseData, setPhase } = useProject();
  const p = phaseData.phase3 || {};
  const interventions = p.interventions;

  // Prepopulate once with Gilbert's BEM environmental causes.
  useEffect(() => {
    if (!Array.isArray(interventions)) {
      setPhase("phase3", { ...p, interventions: BEM_DEFAULTS.map((d) => ({ id: uid(), ...d })) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventions]);

  const list = Array.isArray(interventions) ? interventions : [];
  const setList = (l) => setPhase("phase3", { ...p, interventions: l });
  const add = () => setList([...list, { id: uid(), category: "", diagnosis: "", tag: "Non-Instructional" }]);
  const upd = (i, k, v) => { const l = [...list]; l[i] = { ...l[i], [k]: v }; setList(l); };
  const del = (i) => setList(list.filter((_, idx) => idx !== i));

  return (
    <SectionCard
      title="Intervention Diagnosis"
      desc="Prepopulated with Gilbert's BEM environmental causes. Add a descriptive diagnosis and tag each as instructional or non-instructional."
      right={<button data-testid="add-intervention-btn" onClick={add} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Line</button>}
      testid="phase3-interventions"
    >
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-slate-400 flex items-center gap-2"><Stethoscope className="h-4 w-4" /> No interventions yet.</p>}
        {list.map((it, i) => (
          <div key={it.id || i} className="grid sm:grid-cols-[1fr_2fr_auto_auto] gap-2 items-start border border-slate-100 rounded-md p-2" data-testid={`intervention-row-${i}`}>
            <input value={it.category} onChange={(e) => upd(i, "category", e.target.value)} placeholder="Cause / category" className={inputCls} data-testid={`intervention-category-${i}`} />
            <textarea value={it.diagnosis} onChange={(e) => upd(i, "diagnosis", e.target.value)} rows={2} placeholder="Descriptive diagnosis & notes…" className={`${inputCls} resize-y`} data-testid={`intervention-diagnosis-${i}`} />
            <select value={it.tag} onChange={(e) => upd(i, "tag", e.target.value)} className={`${inputCls} w-40 ${it.tag === "Non-Instructional" ? "text-amber-700" : "text-emerald-700"}`} data-testid={`intervention-tag-${i}`}>
              {TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => del(i)} className="text-slate-300 hover:text-red-500 mt-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
